import type { Content } from '@/modules/Content/interface/Content';
import {
  useState,
  useCallback,
  useEffect,
  useRef,
  type FC,
  type ChangeEvent,
  type MouseEvent,
  Fragment,
  useMemo,
} from 'react';
import {
  ChevronDoubleRightOutline,
  FormatSize,
  LiveTvOutline,
  SettingsOutline,
} from '@eqi-investimentos/icons/web';
import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
} from '@eqi-investimentos/react';
import useLocalStorage from '../../../hooks/useLocalstorage';
import { useTimeout } from '@/hooks/useTimeout';
import { formatTime } from '@/utils/date';
import PlayPauseButton from './Controls/PlayPause';
import { Progress } from './Controls/Progress';
import SkipContent from './Controls/SkipContent';
import SkipRewindButton from './Controls/SkipRewind';
import { Volume } from './Controls/Volume';
import PlayerError from './UI/Error';
import KeyAction, { type KeyActionHandle } from './UI/KeyAction/KeyAction';
import { Loader } from './UI/Loader/Loader';
import { VideoPlayerProps } from './VideoPlayer';

export const AudioPlayer: FC<AudioPlayerProps> = ({
  authors,
  src,
  srcHD,
  autoPlay = true,
  nextSlug,
  previousSlug,
  progress,
  hasVideoMode,
  hasTextMode,
  content,
  setTextMode,
  setVideoMode,
  sendWatchProgress,
}) => {
  const [playbackState, setPlaybackState] = useState(false);
  const [volumeState, setVolumeState] = useLocalStorage('video-volume', 1);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [bufferProgress, setBufferProgress] = useState(0);
  const [seekProgress, setSeekProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [seekTooltip, setSeekTooltip] = useState('00:00');
  const [seekTooltipPosition, setSeekTooltipPosition] = useState('');
  const [currentTimeUI, setCurrentTimeUI] = useState('00:00');
  const [playbackRates] = useState([0.5, 0.75, 1, 1.25, 1.5, 2]);
  const [activePlaybackRate, setActivePlaybackRate] = useLocalStorage(
    'video-playbackrate',
    1
  );
  const [displayLoader, setDisplayLoader] = useState(true);
  const [volumeKeyAction, setVolumeKeyAction] = useState(false);
  const [videoError, setVideoError] = useState<MediaError | null>(null);

  const videoRef = useRef<HTMLAudioElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const videoKeyActionRef = useRef<KeyActionHandle>(null);

  const playPromise = useRef<Promise<void>>(null);
  const volumeData = useRef(volumeState || 1);
  const progressSeekData = useRef(0);

  const [setKeyActionVolumeTimeout] = useTimeout();
  const [setLoaderTimeout, clearLoaderTimeout] = useTimeout();

  /**
   * SEND WATCH PROGRESS
   */
  useEffect(() => {
    const interval = setInterval(() => {
      const video = videoRef.current!;

      if (!playbackState) return;

      sendWatchProgress?.(video.currentTime, video.duration);
    }, 10000);

    return () => clearInterval(interval);
  }, [playbackState, sendWatchProgress]);

  /**
   * PLAYBACK
   */

  const togglePlayHandler = useCallback(() => {
    const video = videoRef.current!;

    if (video.paused || video.ended) {
      playPromise.current = video.play();
      return;
    }

    if (!playPromise.current) {
      return;
    }

    playPromise.current?.then(() => {
      video.pause();
    });
  }, []);

  const videoPlayHandler = useCallback(() => {
    setPlaybackState(true);
  }, []);

  const videoPauseHandler = useCallback(() => {
    setPlaybackState(false);
  }, []);

  /**
   * LOADING
   */

  const showLoaderHandler = useCallback(() => {
    setLoaderTimeout(() => setDisplayLoader(true), 300);
  }, [setLoaderTimeout]);

  const hideLoaderHandler = useCallback(() => {
    clearLoaderTimeout();
    setDisplayLoader(false);
  }, [clearLoaderTimeout]);

  /**
   * VOLUME
   */

  const volumeInputHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const video = videoRef.current!;

      video.volume = +event.target.value;
    },
    []
  );

  const volumeChangeHandler = useCallback(() => {
    const video = videoRef.current!;

    setVolumeState(video.volume);

    if (video.volume === 0) {
      video.muted = true;
    } else {
      video.muted = false;
      volumeData.current = video.volume;
    }
  }, [setVolumeState]);

  const toggleMuteHandler = useCallback(() => {
    const video = videoRef.current!;

    if (video.volume !== 0) {
      volumeData.current = video.volume;
      video.volume = 0;
      setVolumeState(0);
    } else {
      video.volume = volumeData.current;
      setVolumeState(volumeData.current);
    }
  }, [setVolumeState]);

  /**
   * TIME
   */

  const timeChangeHandler = useCallback(() => {
    const video = videoRef.current!;

    const duration = video.duration || 0;
    const currentTime = video.currentTime || 0;
    const buffer = video.buffered;

    // Progress
    setCurrentProgress((currentTime / duration) * 100);
    setSeekProgress(currentTime);

    if (duration > 0) {
      for (let i = 0; i < buffer.length; i++) {
        if (
          buffer.start(buffer.length - 1 - i) === 0 ||
          buffer.start(buffer.length - 1 - i) < video.currentTime
        ) {
          setBufferProgress(
            (buffer.end(buffer.length - 1 - i) / duration) * 100
          );
          break;
        }
      }
    }

    // Time
    const formattedCurrentTime = formatTime(Math.round(currentTime));
    setCurrentTimeUI(formattedCurrentTime);
  }, []);

  /**
   * SEEK
   */

  const seekMouseMoveHandler = useCallback((event: MouseEvent) => {
    const video = videoRef.current!;

    const rect = event.currentTarget.getBoundingClientRect();
    const skipTo = (event.nativeEvent.offsetX / rect.width) * video.duration;

    progressSeekData.current = skipTo;

    let formattedTime: string;

    if (skipTo > video.duration) {
      formattedTime = formatTime(video.duration);
    } else if (skipTo < 0) {
      formattedTime = '00:00';
    } else {
      formattedTime = formatTime(skipTo);
      setSeekTooltipPosition(`${event.nativeEvent.offsetX}px`);
    }

    setSeekTooltip(formattedTime);
  }, []);

  const seekInputHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const video = videoRef.current!;

      const skipTo = progressSeekData.current || +event.target.value;

      video.currentTime = skipTo;
      setCurrentProgress((skipTo / video.duration) * 100);
      setSeekProgress(skipTo);
    },
    []
  );

  /**
   * REWIND & SKIP
   */

  const rewindHandler = useCallback(() => {
    const video = videoRef.current!;

    video.currentTime -= 10;

    const rewindContainer = videoKeyActionRef.current!.rewind;
    const rewindElement = rewindContainer.firstElementChild as HTMLElement;

    rewindContainer.animate(
      [{ opacity: 0 }, { opacity: 1 }, { opacity: 1 }, { opacity: 0 }],
      {
        duration: 1000,
        easing: 'ease-out',
        fill: 'forwards',
      }
    );
    rewindElement.animate(
      [
        { opacity: 1, transform: 'translateX(0)' },
        { opacity: 0, transform: `translateX(-20%)` },
      ],
      {
        duration: 1000,
        easing: 'ease-in-out',
        fill: 'forwards',
      }
    );
  }, []);

  const skipHandler = useCallback(() => {
    const video = videoRef.current!;

    video.currentTime += 10;

    const forwardContainer = videoKeyActionRef.current!.skip;
    const forwardElement = forwardContainer.firstElementChild as HTMLElement;

    forwardContainer.animate(
      [{ opacity: 0 }, { opacity: 1 }, { opacity: 1 }, { opacity: 0 }],
      {
        duration: 1000,
        easing: 'ease-out',
        fill: 'forwards',
      }
    );
    forwardElement.animate(
      [
        { opacity: 1, transform: 'translateX(0)' },
        { opacity: 0, transform: `translateX(20%)` },
      ],
      {
        duration: 1000,
        easing: 'ease-in-out',
        fill: 'forwards',
      }
    );
  }, []);

  /**
   * FULLSCREEN
   */

  const toggleFullscreenHandler = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      videoContainerRef.current!.requestFullscreen();
    }
  }, []);

  /**
   * SETTINGS
   */

  const togglePlaybackRateHandler = useCallback(() => {
    const video = videoRef.current!;

    const currentPlayBackRate = video.playbackRate;

    const currentIndex = playbackRates.indexOf(currentPlayBackRate);
    const nextIndex =
      currentIndex + 1 === playbackRates.length ? 0 : currentIndex + 1;

    const playbackRate = playbackRates[nextIndex];

    video.playbackRate = playbackRate;
    setActivePlaybackRate(playbackRate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setActivePlaybackRate]);

  /**
   * KEYBOARD SHORTKUTS
   */

  const keyEventHandler = useCallback(
    (event: KeyboardEvent) => {
      const video = videoRef.current!;

      const activeElement = document.activeElement;

      if (
        !activeElement ||
        (activeElement.localName === 'input' &&
          (activeElement as HTMLInputElement).type !== 'range') ||
        activeElement.localName === 'textarea'
      ) {
        return;
      }

      const { key } = event;

      switch (key) {
        case 'ArrowLeft':
          event.preventDefault();
          rewindHandler();
          break;
        case 'ArrowRight':
          event.preventDefault();
          skipHandler();
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (video.volume + 0.05 > 1) {
            video.volume = 1;
          } else {
            video.volume = +(video.volume + 0.05).toFixed(2);
          }

          setVolumeKeyAction(true);
          setKeyActionVolumeTimeout(() => {
            setVolumeKeyAction(false);
          }, 1500);

          break;
        case 'ArrowDown':
          event.preventDefault();
          if (video.volume - 0.05 < 0) {
            video.volume = 0;
          } else {
            video.volume = +(video.volume - 0.05).toFixed(2);
          }

          setVolumeKeyAction(true);
          setKeyActionVolumeTimeout(() => {
            setVolumeKeyAction(false);
          }, 1500);

          break;
        case ' ':
          event.preventDefault();
          togglePlayHandler();
          break;
      }
    },
    [togglePlayHandler, rewindHandler, skipHandler, setKeyActionVolumeTimeout]
  );

  /**
   * LOAD VIDEO
   */

  const videoLoadedHandler = useCallback(() => {
    const video = videoRef.current!;

    video.volume = volumeState;
    video.playbackRate = activePlaybackRate;

    setVideoDuration(video.duration);
    timeChangeHandler();

    document.addEventListener('keydown', keyEventHandler);

    if (progress) {
      video.currentTime = progress;
    }

    if (autoPlay) playPromise.current = video.play();
  }, [
    autoPlay,
    volumeState,
    activePlaybackRate,
    timeChangeHandler,
    keyEventHandler,
    progress,
  ]);

  /**
   * ERROR HANDLER
   */

  const errorHandler = useCallback(() => {
    const video = videoRef.current!;

    if (video.error) {
      console.error(video.error);
      setVideoError(video.error);
    }
  }, []);

  /**
   * INITIATE PLAYER
   */

  useEffect(() => {
    return () => {
      document.removeEventListener('keydown', keyEventHandler);
    };
  }, [keyEventHandler]);

  /**
   * RENDER
   */

  const settings = useMemo(
    () => [
      {
        icon: <ChevronDoubleRightOutline />,
        description: 'Velocidade',
        value: activePlaybackRate,
        onClick: togglePlaybackRateHandler,
      },
      {
        icon: <LiveTvOutline />,
        description: 'Ir para modo Vídeo',
        onClick: setVideoMode,
        hide: !hasVideoMode,
      },
      {
        icon: <FormatSize />,
        description: 'Ir para modo Texto',
        onClick: setTextMode,
        hide: !hasTextMode,
      },
    ],
    [
      activePlaybackRate,
      hasTextMode,
      hasVideoMode,
      setTextMode,
      setVideoMode,
      togglePlaybackRateHandler,
    ]
  );

  return (
    <Flex
      ref={videoContainerRef}
      cursor="default"
      bg="background.alwaysDark"
      position="relative"
      w="100vw"
      h="32rem"
      color="content.alwaysLight"
      borderRadius="xs"
    >
      <audio
        ref={videoRef}
        src={srcHD ? srcHD : src}
        controls={false}
        onLoadedMetadata={videoLoadedHandler}
        onClick={togglePlayHandler}
        onPlay={videoPlayHandler}
        onPause={videoPauseHandler}
        onVolumeChange={volumeChangeHandler}
        onTimeUpdate={timeChangeHandler}
        onDoubleClick={toggleFullscreenHandler}
        onSeeking={showLoaderHandler}
        onSeeked={hideLoaderHandler}
        onWaiting={showLoaderHandler}
        onCanPlay={hideLoaderHandler}
        onError={errorHandler}
      >
        <track kind="captions" label="No captions available" />
      </audio>
      <Loader on={displayLoader} />
      <KeyAction
        ref={videoKeyActionRef}
        on={volumeKeyAction}
        volume={volumeState}
        onClick={togglePlayHandler}
      />
      <PlayerError error={videoError} />
      <Flex
        position="absolute"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="center"
        h="full"
        w="full"
        bg="rgba(18, 19, 24, 0.8)"
        transition="opacity 300ms ease-out"
        zIndex="15"
      >
        <Flex p="2.75rem" pb="0" w="full" justifyContent="space-between">
          <Flex direction="column" gap="2xs">
            <Text size="h6">{content.title}</Text>
            <Text size="xl" color="content.brandLight">
              {authors}
            </Text>
          </Flex>
          <Menu>
            <MenuButton h="fit-content">
              <SettingsOutline boxSize="2.5rem" />
            </MenuButton>
            <MenuList
              as={Flex}
              flexDir="column"
              p="xl"
              bg="background.alwaysDark"
              gap="xl"
              w="20rem"
            >
              <Text size="xl">Configurações</Text>
              <Flex direction="column" gap="m">
                {settings.map(
                  ({ hide, description, icon, value, onClick }, idx) =>
                    !hide && (
                      <Fragment key={description}>
                        {idx > 0 && <MenuDivider />}
                        <MenuItem
                          as={Button}
                          variant="naked"
                          p="0"
                          gap="s"
                          justifyContent="flex-start"
                          leftIcon={icon}
                          onClick={onClick}
                          backgroundColor="content.alwaysDark"
                        >
                          <Text size="l">
                            {`${description}${value !== undefined ? ' • ' + value : ''}`}
                          </Text>
                        </MenuItem>
                      </Fragment>
                    )
                )}
              </Flex>
            </MenuList>
          </Menu>
        </Flex>
        <Flex
          gap="6xl"
          alignItems="center"
          sx={{ '& svg': { w: '3rem', h: '3rem' } }}
        >
          {!!previousSlug && <SkipContent slug={previousSlug} type="prev" />}
          <SkipRewindButton type="rewind" handleClick={rewindHandler} />
          <PlayPauseButton
            isPlaying={playbackState}
            onToggle={togglePlayHandler}
          />
          <SkipRewindButton type="skip" handleClick={skipHandler} />
          {!!nextSlug && <SkipContent slug={nextSlug} type="next" />}
        </Flex>
        <Flex direction="column" gap="m" w="full">
          <Flex
            justifyContent="space-between"
            alignItems="center"
            w="full"
            px="2.75rem"
          >
            <Flex gap="s" alignItems="center">
              <Volume
                volume={volumeState}
                onToggle={toggleMuteHandler}
                onSeek={volumeInputHandler}
              />
              <Text size="l">
                {`${currentTimeUI} / ${formatTime(videoDuration)}`}
              </Text>
            </Flex>
          </Flex>
          <Flex w="full" py="xs">
            <Progress
              bufferProgress={bufferProgress}
              currentProgress={currentProgress}
              videoDuration={videoDuration}
              seekProgress={seekProgress}
              seekTooltip={seekTooltip}
              seekTooltipPosition={seekTooltipPosition}
              onHover={seekMouseMoveHandler}
              onSeek={seekInputHandler}
            />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

type AudioPlayerProps = Omit<
  VideoPlayerProps,
  'hasAudioMode' | 'setAudioMode'
> & {
  authors: string;
  content: Content;
  hasVideoMode: boolean;
  setVideoMode: () => void;
};
